
import React, { useContext, useEffect, useState } from 'react';
import { PageContext } from '../PageContext';
import { Page, Puzzle, defaultImageEdits } from '../types';
import { useAppContext } from '../AppContext';
import Footer from '../components/Footer';
import PostCard from '../components/PostCard';
import { Timestamp as TimestampValue } from '../firebase';

// --- MOCK DATA FOR SCREENSHOT RECREATION ---

// 1. Unsolved Puzzle (Dog)
const dogPuzzle: Puzzle = {
    id: 'demo-dog',
    creatorId: 'demo-h',
    creatorProfile: { 
        displayName: 'H', 
        photoURL: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?q=80&w=200' 
    },
    imageUrl: 'https://images.unsplash.com/photo-1543466835-00a7907e9de1?q=80&w=1000', // Dog with tongue
    clue: '_______________________',
    answer: 'DANTE', 
    category: 'Which film is it?',
    createdAt: TimestampValue.now(),
    likes: ['a'], dislikes: [], hearts: ['a'], comments: [],
    imageEdits: defaultImageEdits,
    postColor: '#2e1065',
};

// --- COMPONENTS ---

const FeatureCard: React.FC<{ icon: string; title: string; desc: string }> = ({ icon, title, desc }) => (
    <div className="bg-white/5 border border-white/10 p-6 rounded-2xl backdrop-blur-sm hover:bg-white/10 transition-all hover:-translate-y-1">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center text-2xl text-purple-300 mb-4 border border-white/10">
            <i className={`ph-duotone ph-${icon}`}></i>
        </div>
        <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
        <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
    </div>
);

const RoadmapItem: React.FC<{ title: string; desc: string; icon: string; date: string }> = ({ title, desc, icon, date }) => (
    <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-indigo-900/50 border border-indigo-500/30 flex items-center justify-center text-indigo-300 text-2xl shadow-[0_0_15px_rgba(99,102,241,0.2)]">
            <i className={`ph-duotone ph-${icon}`}></i>
        </div>
        <div>
            <h4 className="text-white font-bold text-lg flex items-center gap-2">
                {title} <span className="text-[10px] bg-indigo-500/20 text-indigo-300 px-2 py-0.5 rounded-full border border-indigo-500/30 uppercase tracking-wide">{date}</span>
            </h4>
            <p className="text-gray-400 text-sm mt-1">{desc}</p>
        </div>
    </div>
);

const ReviewCard: React.FC<{ name: string; review: string; stars: number; color: string }> = ({ name, review, stars, color }) => (
    <div className="bg-white/5 border border-white/10 p-6 rounded-2xl relative">
        <div className="flex items-center gap-2 mb-3">
            <div className={`w-8 h-8 rounded-full ${color} flex items-center justify-center font-bold text-white text-xs`}>
                {name.charAt(0)}
            </div>
            <span className="font-bold text-white text-sm">{name}</span>
            <div className="flex ml-auto text-yellow-400 text-xs">
                {Array.from({length: stars}).map((_, i) => <i key={i} className="ph-fill ph-star"></i>)}
            </div>
        </div>
        <p className="text-gray-300 text-sm italic leading-relaxed">"{review}"</p>
    </div>
);

const PhoneFrame: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="relative mx-auto w-[240px] h-[500px] bg-gray-900 rounded-[2rem] border-[6px] border-gray-800 shadow-2xl overflow-hidden flex flex-col transform hover:scale-105 transition-transform duration-300">
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-5 bg-gray-800 rounded-b-lg z-20"></div>
        {/* Content */}
        <div className="flex-grow overflow-hidden relative h-full bg-gray-100 flex flex-col">
            {children}
        </div>
    </div>
);

const MockBottomNav: React.FC<{ active?: string }> = ({ active = 'home' }) => (
    <div className="bg-[#2e1065] p-2 pb-4 flex justify-between items-center text-gray-400 shrink-0 border-t border-white/10">
        <i className={`ph-duotone ph-house text-xl ${active === 'home' ? 'text-pink-500' : ''}`}></i>
        <i className={`ph-duotone ph-magnifying-glass text-xl ${active === 'search' ? 'text-pink-500' : ''}`}></i>
        <i className={`ph-duotone ph-list-bullets text-xl ${active === 'cats' ? 'text-pink-500' : ''}`}></i>
        <i className={`ph-duotone ph-user-circle text-xl ${active === 'profile' ? 'text-pink-500' : ''}`}></i>
        <i className={`ph-duotone ph-users-three text-xl ${active === 'groups' ? 'text-pink-500' : ''}`}></i>
        <i className={`ph-duotone ph-bell text-xl ${active === 'notifs' ? 'text-pink-500' : ''}`}></i>
    </div>
);

const ShowcaseItem: React.FC<{ 
    title: string; 
    description: string; 
    children: React.ReactNode 
}> = ({ title, description, children }) => (
    <div className="flex flex-col items-center text-center">
        <div className="mb-6 filter drop-shadow-2xl">
            {children}
        </div>
        <h3 className="text-2xl font-bold text-white mb-3 aurora-text">{title}</h3>
        <p className="text-gray-400 text-sm leading-relaxed max-w-xs mx-auto">{description}</p>
    </div>
);

const LandingPage: React.FC = () => {
    const { appConfig } = useAppContext();
    const pageContext = useContext(PageContext);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <div className="login-bg min-h-screen w-full flex flex-col relative overflow-x-hidden font-sans">
             <style>{`
                .snowflake {
                    position: absolute;
                    top: -10px;
                    background: white;
                    border-radius: 50%;
                    opacity: 0.6;
                    animation: fall linear infinite;
                    pointer-events: none;
                }
                @keyframes fall {
                    to { transform: translateY(110vh); }
                }
                @keyframes spin-y {
                    0% { transform: rotateY(0deg); }
                    100% { transform: rotateY(360deg); }
                }
                .logo-spin-3d {
                    animation: spin-y 6s linear infinite;
                    transform-style: preserve-3d;
                }
                .perspective-container {
                    perspective: 1000px;
                }
            `}</style>
            
            {/* Background Snowflakes */}
            {Array.from({ length: 30 }).map((_, i) => (
                <div key={i} className="snowflake" style={{ left: `${Math.random() * 100}%`, width: `${Math.random() * 3 + 2}px`, height: `${Math.random() * 3 + 2}px`, animationDuration: `${Math.random() * 5 + 8}s`, animationDelay: `-${Math.random() * 5}s` }}/>
            ))}

            {/* Sticky Nav */}
            <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 px-6 py-4 flex justify-between items-center ${scrolled ? 'bg-black/80 backdrop-blur-md border-b border-white/10' : 'bg-transparent'}`}>
                <div className="flex items-center gap-2 perspective-container">
                    {appConfig.logoUrl && <img src={appConfig.logoUrl} alt="Logo" className="w-8 h-8 object-contain logo-spin-3d" />}
                    <span className="text-white font-bold text-lg hidden sm:block" style={{ fontFamily: "'Caveat', cursive" }}>What Was It?</span>
                </div>
                <div className="flex gap-4">
                    <button onClick={() => pageContext?.switchPage(Page.Login, 'Sign In')} className="text-white font-semibold hover:text-[var(--accent-pink)]">Log In</button>
                    <button onClick={() => pageContext?.switchPage(Page.Signup, 'Sign Up')} className="bg-white text-purple-900 font-bold px-4 py-2 rounded-full text-sm hover:scale-105 transition-transform">Sign Up</button>
                </div>
            </nav>

            {/* Hero Section */}
            <div className="flex-shrink-0 pt-32 pb-16 px-6 z-10 perspective-container max-w-7xl mx-auto w-full text-center">
                <div className="inline-block px-4 py-1 rounded-full bg-white/10 border border-white/20 text-yellow-300 text-sm font-bold mb-6 animate-pulse">
                    🚀 The Ultimate Social Guessing Game
                </div>
                <h1 className="text-5xl md:text-7xl font-extrabold mb-6 tracking-tight text-white leading-tight">
                    Guess. Create.<br/>
                    <span className="aurora-text shimmering-text">Solve.</span>
                </h1>
                
                {/* NEW: Extended Description & Mission Statement */}
                <div className="max-w-3xl mx-auto space-y-6 mb-12 text-gray-200 leading-relaxed font-light text-lg">
                    <p>
                        <strong>What Was It?</strong> is the ultimate social guessing game that transforms everyday photos into mind-bending mysteries. Whether you are snapping a macro shot of a household object or using our AI filters to obscure a famous landmark, the challenge is simple yet addictive: stump your friends and the global community. With private groups for family game nights, global leaderboards for the competitive edge, and an AI Creator Studio, there is a way to play for everyone.
                    </p>
                    <p className="opacity-90">
                        We are on a mission to redefine social gaming by blending creativity with connection. We are building a platform where curiosity thrives, expanding beyond images into <strong>Audio Games</strong>, <strong>Video Streaming</strong> challenges, and interactive <strong>AI-hosted events</strong>. Join us as we grow, and help shape the future of how the world plays together.
                    </p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
                    <button onClick={() => pageContext?.switchPage(Page.Signup, 'Sign Up')} className="px-10 py-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold rounded-full shadow-xl hover:scale-105 transition-transform text-lg flex items-center justify-center gap-2">
                        <i className="ph-bold ph-play-circle"></i> Play Free Now
                    </button>
                    <button onClick={() => pageContext?.switchPage(Page.Home, 'Home')} className="px-10 py-4 bg-white/10 border border-white/20 text-white font-bold rounded-full hover:bg-white/20 transition-all text-lg backdrop-blur-sm">
                        View Demo
                    </button>
                </div>
            </div>

            {/* FEATURE SHOWCASE GRID */}
            <div className="max-w-7xl mx-auto px-6 pb-20 relative z-20">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 lg:gap-8">
                    
                    {/* Item 1: Solving */}
                    <ShowcaseItem 
                        title="Solve Daily Mysteries" 
                        description="Can you guess what's in the picture? Use clues, ask friends, and earn points for every correct answer."
                    >
                        <PhoneFrame>
                            <div className="bg-[#2e1065] h-full flex flex-col font-sans">
                                <div className="p-4 text-white flex items-center justify-center border-b border-white/10 pt-8 shrink-0">
                                    <span className="font-bold tracking-widest text-pink-300">PUZZLE</span>
                                </div>
                                <div className="p-2 flex-grow overflow-y-auto custom-scrollbar transform scale-95 origin-top">
                                    <PostCard puzzle={dogPuzzle} isClickable={false} isPreview={true} />
                                </div>
                                <MockBottomNav active="home" />
                            </div>
                        </PhoneFrame>
                    </ShowcaseItem>

                    {/* Item 2: Community Groups */}
                    <ShowcaseItem 
                        title="Join Communities" 
                        description="Create private groups for your family or join public communities like 'Cinema Buffs' or 'Travelers'."
                    >
                        <PhoneFrame>
                            <div className="bg-gray-100 h-full flex flex-col font-sans">
                                <div className="bg-[#2e1065] p-4 pt-8 text-white shrink-0">
                                    <h2 className="text-xl font-bold aurora-text">GROUPS</h2>
                                    <div className="mt-4 bg-white/10 rounded-lg p-2 flex items-center gap-2">
                                        <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center text-[#2e1065]">
                                            <i className="ph-bold ph-plus"></i>
                                        </div>
                                        <span className="font-bold text-yellow-200 text-sm">Create New Group</span>
                                    </div>
                                </div>
                                <div className="p-3 space-y-3 flex-grow overflow-y-auto custom-scrollbar">
                                    <div>
                                        <h3 className="font-bold text-gray-700 text-xs mb-2">My Groups</h3>
                                        <div className="w-full h-24 rounded-xl relative overflow-hidden shadow-lg">
                                            <img src="https://images.unsplash.com/photo-1514933651103-005eec06c04b" className="w-full h-full object-cover" />
                                            <div className="absolute inset-0 bg-black/40 flex items-end p-2">
                                                <span className="text-white font-bold text-sm">Friday Pub Quiz</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-gray-700 text-xs mb-2">Recommended</h3>
                                        <div className="flex gap-2 overflow-hidden">
                                            <div className="w-24 h-24 bg-gray-300 rounded-xl relative overflow-hidden flex-shrink-0">
                                                <img src="https://images.unsplash.com/photo-1489599849927-2ee91cede3ba" className="w-full h-full object-cover" />
                                                <span className="absolute bottom-1 left-1 text-white font-bold text-[10px]">Cinema</span>
                                            </div>
                                            <div className="w-24 h-24 bg-gray-300 rounded-xl relative overflow-hidden flex-shrink-0">
                                                <img src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e" className="w-full h-full object-cover" />
                                                <span className="absolute bottom-1 left-1 text-white font-bold text-[10px]">Travel</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <MockBottomNav active="groups" />
                            </div>
                        </PhoneFrame>
                    </ShowcaseItem>

                    {/* Item 3: Profile & Stats */}
                    <ShowcaseItem 
                        title="Climb the Ranks" 
                        description="Earn badges, track your stats, and see how you compare against friends on the global leaderboard."
                    >
                        <PhoneFrame>
                            <div className="h-full bg-gray-100 flex flex-col font-sans">
                                <div className="flex-grow overflow-y-auto custom-scrollbar">
                                    <div className="bg-[var(--primary-dark-purple)] p-4 pt-8 text-white text-center rounded-b-3xl relative pb-8 shrink-0">
                                        <h2 className="text-sm font-bold tracking-widest text-pink-300 mb-4">PROFILE</h2>
                                        <div className="flex flex-col items-center">
                                            <div className="relative">
                                                <div className="w-20 h-20 rounded-full p-1 bg-gradient-to-r from-green-400 to-blue-500">
                                                    {appConfig.logoUrl && <img src={appConfig.logoUrl} className="w-full h-full object-contain rounded-full bg-white" />}
                                                </div>
                                                <span className="absolute bottom-0 right-0 w-5 h-5 bg-green-500 border-4 border-[var(--primary-dark-purple)] rounded-full"></span>
                                            </div>
                                            <h3 className="text-lg font-bold mt-2">Puzzle Master</h3>
                                            <div className="flex gap-6 mt-3">
                                                <div className="text-center"><p className="font-bold text-md">124</p><p className="text-[10px] opacity-70">Solved</p></div>
                                                <div className="text-center"><p className="font-bold text-md">2,405</p><p className="text-[10px] opacity-70">Score</p></div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-4 space-y-2 mt-2">
                                        <div className="bg-white p-3 rounded-lg shadow-sm flex items-center justify-between">
                                            <span className="font-bold text-sm text-gray-700">Rank #42</span>
                                            <i className="ph-duotone ph-trophy text-yellow-500"></i>
                                        </div>
                                        <div className="bg-white p-3 rounded-lg shadow-sm flex items-center justify-between">
                                            <span className="font-bold text-sm text-gray-700">Daily Streak</span>
                                            <span className="font-bold text-orange-500">12 Days 🔥</span>
                                        </div>
                                    </div>
                                </div>
                                <MockBottomNav active="profile" />
                            </div>
                        </PhoneFrame>
                    </ShowcaseItem>

                </div>
            </div>
            
            {/* Main Content Area */}
            <div className="flex-grow z-10 bg-black/40 backdrop-blur-lg border-t border-white/10">
                <div className="max-w-6xl mx-auto px-6 py-20 space-y-32">

                    {/* NEW: Reviews Section */}
                    <section>
                        <div className="text-center mb-12">
                            <h2 className="text-3xl font-bold text-white mb-2">What Players Are Saying</h2>
                            <p className="text-gray-400">Join thousands of happy puzzlers.</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <ReviewCard 
                                name="Mitch"
                                stars={5}
                                color="bg-blue-600"
                                review="Really fun and addictive hours of fun guessing with my kids. Highly recommended for family game night!"
                            />
                            <ReviewCard 
                                name="Sarah_J"
                                stars={5}
                                color="bg-pink-600"
                                review="I love the private groups feature. We use it for our office weekly challenge. It gets surprisingly competitive!"
                            />
                            <ReviewCard 
                                name="PuzzleKing99"
                                stars={4}
                                color="bg-purple-600"
                                review="The AI hints are a game changer. Super creative and helps when you're stuck without giving the answer away."
                            />
                            <ReviewCard 
                                name="LisaM"
                                stars={5}
                                color="bg-emerald-600"
                                review="Best app for creative minds. I love editing my photos to make them tricky. 10/10."
                            />
                        </div>
                    </section>

                    {/* Roadmap / Future Updates */}
                    <section className="bg-white/5 rounded-3xl p-8 md:p-12 border border-white/10">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                            <div>
                                <h2 className="text-3xl font-bold text-white mb-6">Updates to Come</h2>
                                <p className="text-gray-300 mb-8 text-lg">We are constantly expanding the universe of What Was It? Here is what's launching soon.</p>
                                <div className="space-y-6">
                                    <RoadmapItem 
                                        title="Audio Games" 
                                        desc="Listen closely! Solve mysteries based on sound clips and voice clues in groups." 
                                        icon="speaker-high" 
                                        date="Coming Soon"
                                    />
                                    <RoadmapItem 
                                        title="Video & Streaming Games" 
                                        desc="Live streaming games and video-based puzzles. Watch, guess, and win in real-time." 
                                        icon="video-camera" 
                                        date="In Development"
                                    />
                                    <RoadmapItem 
                                        title="AI Creator Games" 
                                        desc="Advanced AI modes where the computer generates unique mysteries for you to solve." 
                                        icon="robot" 
                                        date="Q3 2025"
                                    />
                                    <RoadmapItem 
                                        title="More Categories" 
                                        desc="New topics like 'History', 'Sci-Fi', 'Food', and 'Macro Photography' are being added weekly." 
                                        icon="squares-four" 
                                        date="Weekly Updates"
                                    />
                                </div>
                            </div>
                            <div className="hidden lg:flex items-center justify-center relative">
                                <div className="w-64 h-64 bg-gradient-to-tr from-purple-500 to-pink-500 rounded-full blur-[80px] opacity-30 absolute"></div>
                                <div className="relative z-10 text-center space-y-6">
                                    <div className="bg-white/10 p-4 rounded-2xl backdrop-blur-md border border-white/10 transform rotate-6 hover:rotate-0 transition-transform duration-500">
                                        <i className="ph-duotone ph-game-controller text-5xl text-purple-400 mb-2"></i>
                                        <p className="text-white font-bold">Interactive Play</p>
                                    </div>
                                    <div className="bg-white/10 p-4 rounded-2xl backdrop-blur-md border border-white/10 transform -rotate-6 hover:rotate-0 transition-transform duration-500">
                                        <i className="ph-duotone ph-broadcast text-5xl text-pink-400 mb-2"></i>
                                        <p className="text-white font-bold">Live Streaming</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Features Grid */}
                    <section>
                        <h2 className="text-3xl font-bold text-white text-center mb-12">Current Features</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <FeatureCard 
                                icon="paint-brush-broad"
                                title="Snap & Edit"
                                desc="Take a photo of anything. Use our powerful in-app editor to crop, filter, and obscure the image just enough to make it a challenge."
                            />
                            <FeatureCard 
                                icon="users-three"
                                title="Private Groups"
                                desc="Create invite-only lobbies for your inner circle or join public communities based on interests."
                            />
                            <FeatureCard 
                                icon="brain"
                                title="AI Powered"
                                desc="Running out of ideas? Use our Gemini-powered AI to generate unique images or witty clues."
                            />
                        </div>
                    </section>

                    {/* Final CTA */}
                    <section className="text-center py-16 bg-gradient-to-r from-purple-900/80 to-pink-900/80 rounded-3xl border border-white/20 shadow-2xl relative overflow-hidden">
                        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-20"></div>
                        <div className="relative z-10 px-4">
                            <h2 className="text-4xl font-bold text-white mb-6">Ready to Challenge Your Brain?</h2>
                            <p className="text-gray-200 mb-8 max-w-lg mx-auto text-xl">Join thousands of users today. It's free, safe, and wildly addictive.</p>
                            <button onClick={() => pageContext?.switchPage(Page.Signup, 'Sign Up')} className="px-12 py-5 bg-white text-purple-900 font-bold rounded-full shadow-2xl hover:shadow-white/20 hover:scale-105 transition-all text-xl">
                                Create Free Account
                            </button>
                            <p className="mt-6 text-sm text-gray-400">No credit card required • Instant Access</p>
                        </div>
                    </section>

                </div>
                
                {/* Updated Footer */}
                <div className="bg-black/60 border-t border-white/10 py-12">
                    <div className="max-w-6xl mx-auto px-6 text-center">
                        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                            <div className="flex items-center gap-2 perspective-container">
                                {appConfig.logoUrl && <img src={appConfig.logoUrl} className="w-8 h-8 object-contain logo-spin-3d" />}
                                <span className="text-white font-bold text-xl" style={{ fontFamily: "'Caveat', cursive" }}>What Was It?</span>
                            </div>
                            <div className="text-gray-400 text-sm">
                                <p>Official Website & Contact:</p>
                                <a href="mailto:whatwasitltd@gmail.com" className="text-white font-bold hover:text-[var(--accent-pink)] transition-colors">whatwasitltd@gmail.com</a>
                            </div>
                        </div>
                        <Footer />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LandingPage;
